import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { StyleObj } from '../../../../@types';
import { removeBetslipBet } from '../../../../stores/betslip/selectors';
import type { BetslipEvent } from '../../../../types/betslip';
import { OddsChangeStatus } from '../../../../helpers/betslipMessageParser';
import { BetDetails } from '../../../atoms/BetDetails';
import OddsDisplay from '../../../atoms/OddsDisplay';
import useLocalization from '../../../../hooks/useLocalization';

const DELETE_BUTTON_WIDTH = 95;

const getOddsColors = (oddsChange?: OddsChangeStatus) => {
  const isChanged = oddsChange === OddsChangeStatus.increased || oddsChange === OddsChangeStatus.decreased;

  return {
    bg:
      oddsChange === OddsChangeStatus.increased
        ? 'success.main'
        : oddsChange === OddsChangeStatus.decreased
          ? 'error.600'
          : 'transparent',
    color: isChanged ? 'text.primary' : 'neutral.800',
  };
};

type StylesProps = {
  isDisabled: boolean;
  showDelete: boolean;
};

const makeStyles = ({ isDisabled, showDelete }: StylesProps): StyleObj => ({
  container: {
    position: 'relative',
    cursor: 'pointer',
    backgroundColor: (theme) => theme.palette.neutral[100],
    borderTop: '1px solid',
    borderColor: 'neutral.200',
    opacity: isDisabled ? 0.5 : 1,
  },
  contentWrapper: {
    width: '100%',
    px: 1,
    py: 0.75,
    transition: 'transform 0.3s ease',
  },
  header: {
    textAlign: 'left',
    '& p': { fontSize: 15 },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  deleteButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    height: '100%',
    width: DELETE_BUTTON_WIDTH,
    minWidth: DELETE_BUTTON_WIDTH,
    borderRadius: 0,
    backgroundColor: 'error.700',
    color: 'text.primary',
    typography: 'body2',
    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 2,
    transform: showDelete ? 'translateX(0)' : 'translateX(100%)',
    '&:hover': { backgroundColor: 'error.800' },
  },
});

type BetslipItemProps = {
  bets: BetslipEvent[];
  onRemoveBet?: (outcomeId: string) => void;
};

const BetslipItem = ({ bets, onRemoveBet }: BetslipItemProps) => {
  const [showDelete, setShowDelete] = useState(false);

  const { t } = useLocalization();

  if (!bets || bets.length === 0) return null;

  const isDisabled = bets.some((bet) => bet.disabled);
  const eventName = bets[0].eventName;
  const styles = makeStyles({ isDisabled, showDelete });

  const handleRemoveAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDelete(false);

    bets.forEach((bet) => {
      removeBetslipBet(bet.outcomeId);
      onRemoveBet?.(bet.outcomeId);
    });
  };

  return (
    <Box sx={styles.container} onClick={() => setShowDelete((prev) => !prev)}>
      <Box sx={styles.contentWrapper}>
        <Box sx={styles.header}>
          <Typography
            sx={{
              color: 'neutral.800',
            }}
          >
            {eventName}
          </Typography>
        </Box>

        <Stack>
          {bets.map((bet) => {
            const { bg, color } = getOddsColors(bet.oddsChange);

            return (
              <Box key={bet.outcomeId} sx={styles.row}>
                <Stack
                  direction='row'
                  spacing={0.25}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='body3'
                    sx={{
                      color: 'neutral.600',
                      textAlign: 'left',
                    }}
                  >
                    <BetDetails.Market
                      marketName={bet.marketName}
                      marketShortName={bet.marketShortName}
                      marketType={bet.marketType.name}
                      color='inherit'
                      variant='inherit'
                    />
                    {' ('}
                    <BetDetails.Outcome
                      outcomeName={bet.outcomeName}
                      outcomeShortName={bet.outcomeShortName}
                      {...bet.properties}
                      marketName={bet.marketName}
                      marketTypeName={bet.marketType.name}
                      marketDisplayType={bet.marketDisplayType}
                      eventName={bet.eventName}
                    />
                    {!!bet.outcomeShortName && (
                      <BetDetails.SpecialValues
                        outcomeName={bet.outcomeName}
                        specialValues={bet.specialValues}
                        marketTypeName={bet.marketType.name}
                        specialValueModel={bet.specialValues?.[0]?.model}
                        sportId={bet.marketType.sportId}
                      />
                    )}
                    {')'}
                  </Typography>
                </Stack>

                <OddsDisplay
                  value={bet.odds}
                  variant='body2'
                  sx={{
                    color: color,
                    bgcolor: bg,
                    px: 0.75,
                    height: 'fit-content',
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Button sx={styles.deleteButton} onClick={handleRemoveAll}>
        {t('delete')}
      </Button>
    </Box>
  );
};

export default BetslipItem;
